import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { FaLayerGroup, FaSave, FaTractor, FaUsers } from "react-icons/fa";

import {
  createMachineGroupId,
  loadServiceOrderMachineGroups,
  loadVisibleServiceOrderMachines,
  machinesInVisibleGroup,
  nextVisibleMachineIdents,
  saveServiceOrderMachineGroups,
  saveVisibleServiceOrderMachines,
} from "./serviceOrderMachineGroups";

function machineIdentsFromGroup(group) {
  return (group && group.machineIdents ? group.machineIdents : [])
    .map((ident) => String(ident || "").trim())
    .filter(Boolean);
}

function ServiceOrderMachineSelector({
  farmId,
  machines,
  selectedMachineIdents,
  onSelectedMachineIdentsChange,
  requireMachine,
  error,
  t,
}) {
  const [customGroups, setCustomGroups] = useState(() =>
    loadServiceOrderMachineGroups(farmId)
  );
  const [visibleMachineIdents, setVisibleMachineIdents] = useState(() =>
    loadVisibleServiceOrderMachines(farmId)
  );
  const [groupDraft, setGroupDraft] = useState(null);
  const [groupOpen, setGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [showSaveGroup, setShowSaveGroup] = useState(false);

  useEffect(() => {
    setCustomGroups(loadServiceOrderMachineGroups(farmId));
    setVisibleMachineIdents(loadVisibleServiceOrderMachines(farmId));
    setShowSaveGroup(false);
    setNewGroupName("");
    setGroupOpen(false);
  }, [farmId]);

  const sortedMachines = useMemo(
    () =>
      (machines || [])
        .slice()
        .sort((left, right) =>
          String(left.ident || "").localeCompare(
            String(right.ident || ""),
            "pt-BR"
          )
        ),
    [machines]
  );

  const allMachineIdents = useMemo(
    () =>
      sortedMachines
        .map((machine) => String(machine.ident || "").trim())
        .filter(Boolean),
    [sortedMachines]
  );

  const listedMachines = useMemo(
    () => machinesInVisibleGroup(sortedMachines, visibleMachineIdents),
    [sortedMachines, visibleMachineIdents]
  );

  const listedMachineIdents = useMemo(
    () =>
      listedMachines
        .map((machine) => String(machine.ident || "").trim())
        .filter(Boolean),
    [listedMachines]
  );

  const selectedSet = useMemo(
    () => new Set(selectedMachineIdents),
    [selectedMachineIdents]
  );

  const persistGroups = (groups) => {
    setCustomGroups(groups);
    saveServiceOrderMachineGroups(farmId, groups);
  };

  const setSelection = (idents) => {
    const next = Array.from(
      new Set(
        (idents || [])
          .map((ident) => String(ident || "").trim())
          .filter(Boolean)
      )
    );
    onSelectedMachineIdentsChange(next);
  };

  const toggleMachine = (ident) => {
    const normalized = String(ident || "").trim();
    if (!normalized) return;
    if (selectedSet.has(normalized)) {
      setSelection(selectedMachineIdents.filter((item) => item !== normalized));
      return;
    }
    setSelection([...selectedMachineIdents, normalized]);
  };

  const handleSaveGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    if (selectedMachineIdents.length === 0) return;

    const nextGroups = [
      ...customGroups,
      {
        id: createMachineGroupId(),
        name,
        machineIdents: selectedMachineIdents.slice(),
      },
    ];
    persistGroups(nextGroups);
    setVisibleMachineIdents(selectedMachineIdents.slice());
    saveVisibleServiceOrderMachines(farmId, selectedMachineIdents.slice());
    setNewGroupName("");
    setShowSaveGroup(false);
  };

  const applyCustomGroup = (group) => {
    const idents = machineIdentsFromGroup(group);
    setSelection(idents);
    setVisibleMachineIdents(idents);
    saveVisibleServiceOrderMachines(farmId, idents);
  };

  const openVisibleGroup = () => {
    setGroupDraft(visibleMachineIdents);
    setGroupOpen(true);
  };

  const toggleDraftMachine = (ident) => {
    setGroupDraft((current) =>
      nextVisibleMachineIdents(allMachineIdents, current, ident)
    );
  };

  const saveVisibleGroup = () => {
    setVisibleMachineIdents(groupDraft);
    saveVisibleServiceOrderMachines(farmId, groupDraft);
    setGroupOpen(false);
  };

  const showAllMachines = () => {
    setVisibleMachineIdents(null);
    saveVisibleServiceOrderMachines(farmId, null);
    setGroupDraft(null);
    setGroupOpen(false);
  };

  const removeCustomGroup = (groupId) => {
    persistGroups(customGroups.filter((group) => group.id !== groupId));
  };

  return (
    <div
      className={`service-order-machine-selector${
        error ? " service-order-machine-selector--error" : ""
      }`}
    >
      <div className="service-order-machine-selector__toolbar">
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setSelection(listedMachineIdents)}
          disabled={listedMachineIdents.length === 0}
        >
          {t("Marcar todas")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setSelection([])}
          disabled={selectedMachineIdents.length === 0}
        >
          {t("Limpar seleção")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setShowSaveGroup((current) => !current)}
          disabled={selectedMachineIdents.length === 0}
        >
          <FaSave style={{ marginRight: 4 }} />
          {t("Salvar grupo")}
        </button>
        <button
          type="button"
          className={`service-order-machine-selector__group${
            Array.isArray(visibleMachineIdents) ? " is-on" : ""
          }`}
          onClick={openVisibleGroup}
          title={t("Gerar grupo")}
          aria-label={t("Gerar grupo")}
        >
          <FaUsers />
        </button>
      </div>

      {Array.isArray(visibleMachineIdents) ? (
        <p className="service-order-machine-selector__group-note">
          {t("Só as máquinas deste grupo aparecem na lista.")}{" "}
          <button type="button" onClick={showAllMachines}>
            {t("Ver todas as máquinas")}
          </button>
        </p>
      ) : null}

      {selectedMachineIdents.length > 0 ? (
        <p className="service-order-machine-selector__summary">
          {selectedMachineIdents.length}{" "}
          {t("máquina(s) selecionada(s)")}: {selectedMachineIdents.join(", ")}
        </p>
      ) : (
        <p className="service-order-machine-selector__summary is-empty">
          {requireMachine
            ? t("Selecione ao menos uma máquina.")
            : t("Nenhuma máquina selecionada (qualquer equipamento).")}
        </p>
      )}

      {showSaveGroup ? (
        <div className="service-order-machine-selector__save-group">
          <input
            type="text"
            className="form-control input-sm"
            value={newGroupName}
            placeholder={t("Nome do grupo (ex.: Pulverização, Plantio)")}
            onChange={(event) => setNewGroupName(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveGroup}
            disabled={!newGroupName.trim()}
          >
            {t("Salvar")}
          </button>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={() => {
              setShowSaveGroup(false);
              setNewGroupName("");
            }}
          >
            {t("Cancelar")}
          </button>
        </div>
      ) : null}

      {customGroups.length > 0 ? (
        <div className="service-order-machine-selector__custom-groups">
          <strong>
            <FaLayerGroup style={{ marginRight: 6 }} />
            {t("Meus grupos")}
          </strong>
          <div className="service-order-machine-selector__custom-groups-list">
            {customGroups.map((group) => (
              <div key={group.id} className="service-order-machine-custom-group">
                <button
                  type="button"
                  className="btn btn-default btn-xs"
                  onClick={() => applyCustomGroup(group)}
                  title={machineIdentsFromGroup(group).join(", ")}
                >
                  {group.name} ({machineIdentsFromGroup(group).length})
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-xs service-order-machine-custom-group__remove"
                  onClick={() => removeCustomGroup(group.id)}
                >
                  {t("Remover")}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {sortedMachines.length === 0 ? (
        <p className="create-schedule-empty-operators">
          {t("Nenhuma máquina cadastrada nesta fazenda.")}
        </p>
      ) : listedMachines.length === 0 ? (
        <p className="create-schedule-empty-operators">
          {t("Nenhuma máquina neste grupo.")}{" "}
          <button type="button" className="btn btn-link btn-xs" onClick={showAllMachines}>
            {t("Ver todas as máquinas")}
          </button>
        </p>
      ) : (
        <div className="service-order-machine-subgroup__items">
          {listedMachines.map((machine) => (
            <label
              key={machine.id}
              className="create-schedule-operator-item service-order-machine-item"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(String(machine.ident || "").trim())}
                onChange={() => toggleMachine(machine.ident)}
              />
              <span>
                <FaTractor
                  style={{ marginRight: 6, opacity: 0.7 }}
                  aria-hidden
                />
                {machine.ident}
                {machine.operator_label ? ` — ${machine.operator_label}` : ""}
              </span>
            </label>
          ))}
        </div>
      )}

      {groupOpen
        ? ReactDOM.createPortal(
            <div className="service-order-machine-group-modal">
              <div
                className="service-order-machine-group-modal__backdrop"
                onClick={() => setGroupOpen(false)}
              />
              <div
                className="service-order-machine-group-modal__card"
                role="dialog"
                aria-labelledby="service-order-machine-group-title"
              >
                <h3 id="service-order-machine-group-title">
                  {t("Quais máquinas aparecem")}
                </h3>
                <p>
                  {t(
                    "Marque as máquinas que devem aparecer nesta lista. As outras ficam ocultas."
                  )}
                </p>
                <label className="service-order-machine-group-modal__all">
                  <input
                    type="checkbox"
                    checked={groupDraft == null}
                    onChange={() => toggleDraftMachine("*")}
                  />
                  {t("Todas")}
                </label>
                <div className="service-order-machine-group-modal__list">
                  {sortedMachines.map((machine) => {
                    const ident = String(machine.ident || "").trim();
                    const checked =
                      groupDraft == null ||
                      groupDraft.some((item) => item === ident);
                    return (
                      <label key={machine.id || ident}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDraftMachine(ident)}
                        />
                        {ident}
                      </label>
                    );
                  })}
                </div>
                <div className="service-order-machine-group-modal__actions">
                  <button type="button" onClick={() => setGroupOpen(false)}>
                    {t("Cancelar")}
                  </button>
                  <button type="button" onClick={showAllMachines}>
                    {t("Ver todas as máquinas")}
                  </button>
                  <button type="button" onClick={saveVisibleGroup}>
                    {t("Salvar")}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default ServiceOrderMachineSelector;
